import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  TruckIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { PageMeta } from '@common/components/core';
import { Button, Input, Card, CardHeader } from '@common/components/ui';
import { configService, ShippingConfig } from '../services/configService';

interface CountryConfig {
  key: string;
  label: string;
  flag: string;
  costField: keyof ShippingConfig;
  thresholdField: keyof ShippingConfig;
}

const countries: CountryConfig[] = [
  {
    key: 'kosovo',
    label: 'Kosovo',
    flag: '🇽🇰',
    costField: 'kosovoShippingCost',
    thresholdField: 'kosovoFreeThreshold',
  },
  {
    key: 'albania',
    label: 'Albania',
    flag: '🇦🇱',
    costField: 'albaniaShippingCost',
    thresholdField: 'albaniaFreeThreshold',
  },
  {
    key: 'north_macedonia',
    label: 'North Macedonia',
    flag: '🇲🇰',
    costField: 'northMacedoniaShippingCost',
    thresholdField: 'northMacedoniaFreeThreshold',
  },
];

const ShippingSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<ShippingConfig | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['shippingConfig'],
    queryFn: () => configService.getShippingConfig(),
    select: (res) => res.data,
  });

  // Sync fetched data to form state
  React.useEffect(() => {
    if (data && !formData) {
      setFormData(data);
    }
  }, [data, formData]);

  const updateMutation = useMutation({
    mutationFn: (config: Partial<ShippingConfig>) =>
      configService.updateShippingConfig(config),
    onSuccess: () => {
      toast.success('Shipping configuration updated successfully');
      queryClient.invalidateQueries({ queryKey: ['shippingConfig'] });
      setIsDirty(false);
    },
    onError: () => {
      toast.error('Failed to update shipping configuration');
    },
  });

  const initMutation = useMutation({
    mutationFn: () => configService.initializeShippingConfig(),
    onSuccess: () => {
      toast.success('Shipping configuration initialized');
      queryClient.invalidateQueries({ queryKey: ['shippingConfig'] });
      setFormData(null);
    },
    onError: () => {
      toast.error('Failed to initialize shipping configuration');
    },
  });

  const handleChange = (field: keyof ShippingConfig, value: string) => {
    if (!formData) return;
    const numValue = parseFloat(value) || 0;
    setFormData({ ...formData, [field]: numValue });
    setIsDirty(true);
  };

  const handleSave = () => {
    if (!formData) return;
    updateMutation.mutate(formData);
  };

  const handleReset = () => {
    if (data) {
      setFormData(data);
      setIsDirty(false);
    }
  };

  if (isLoading) {
    return (
      <>
        <PageMeta
          title="Shipping Settings"
          breadcrumbs={[
            { label: 'Dashboard', href: '/' },
            { label: 'Shipping Settings' },
          ]}
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-6" />
              <div className="space-y-4">
                <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
            </Card>
          ))}
        </div>
      </>
    );
  }

  if (error || !formData) {
    return (
      <>
        <PageMeta
          title="Shipping Settings"
          breadcrumbs={[
            { label: 'Dashboard', href: '/' },
            { label: 'Shipping Settings' },
          ]}
        />
        <Card className="p-8 text-center">
          <TruckIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Shipping configuration not found
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Initialize the shipping configuration to set up fees and free shipping thresholds for each country.
          </p>
          <Button
            onClick={() => initMutation.mutate()}
            loading={initMutation.isPending}
            icon={<ArrowPathIcon className="h-4 w-4" />}
          >
            Initialize Shipping Config
          </Button>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageMeta
        title="Shipping Settings"
        description="Manage shipping fees and free shipping thresholds per country"
        breadcrumbs={[
          { label: 'Dashboard', href: '/' },
          { label: 'Shipping Settings' },
        ]}
        action={
          <div className="flex items-center gap-3">
            {isDirty && (
              <Button variant="outline" size="sm" onClick={handleReset}>
                Discard
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleSave}
              loading={updateMutation.isPending}
              disabled={!isDirty}
            >
              Save Changes
            </Button>
          </div>
        }
      />

      {/* Country Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {countries.map((country) => (
          <Card key={country.key} padded={false}>
            <CardHeader className="bg-gray-50 dark:bg-gray-800/50 rounded-t-xl">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{country.flag}</span>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {country.label}
                </h3>
              </div>
            </CardHeader>

            <div className="p-6 space-y-5">
              {/* Shipping Cost */}
              <div>
                <Input
                  label="Shipping Fee"
                  type="number"
                  min="0"
                  step="0.5"
                  value={formData[country.costField]}
                  onChange={(e) =>
                    handleChange(country.costField, e.target.value)
                  }
                  helperText="Cost charged for delivery"
                />
              </div>

              {/* Free Shipping Threshold */}
              <div>
                <Input
                  label="Free Shipping Threshold"
                  type="number"
                  min="0"
                  step="1"
                  value={formData[country.thresholdField]}
                  onChange={(e) =>
                    handleChange(country.thresholdField, e.target.value)
                  }
                  helperText="Minimum order amount for free shipping"
                />
              </div>

              {/* Summary */}
              <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Orders under{' '}
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    €{formData[country.thresholdField]}
                  </span>{' '}
                  will be charged{' '}
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    €{formData[country.costField]}
                  </span>{' '}
                  for shipping.
                </p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Info Banner */}
      <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <TruckIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-700 dark:text-blue-300">
            <p className="font-medium mb-1">How shipping fees work</p>
            <ul className="list-disc list-inside space-y-1 text-blue-600 dark:text-blue-400">
              <li>
                The shipping fee is added to orders below the free shipping threshold.
              </li>
              <li>
                Orders meeting or exceeding the threshold get free shipping automatically.
              </li>
              <li>
                Customers can see how much more they need to spend for free shipping at checkout.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};

export default ShippingSettings;
